import { useMemo, useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import type { WeekDay } from "Types/WeekDay";
import type { HoursState, DayHours } from "Types/OpeningHours";
import type { MerchantFacilityHoursDto } from "Infrastructure/Api/Api";
import {
  dtoToState,
  stateToDto,
  createEmptyHoursState,
} from "Utils/OpeningHoursUtils";
import { Resources, useResource } from "Translations/Resources";
import { StyledValidationText } from "Components/Shared/FormStyles";
import { BulkEditPanel } from "./BulkEditPanel/BulkEditPanel";
import { OpeningHoursSummary } from "./OpeningHoursSummary";
import type { BulkHoursConfig } from "./types";

type Props = {
  value: Array<MerchantFacilityHoursDto>;
  onChange: (value: Array<MerchantFacilityHoursDto>) => void;
  label?: string;
  errorMessage?: string;
};

const RESOURCES = Resources.Components.Merchant.Facilities.Form.OpeningHours;

export const OpeningHoursInput = ({
  value,
  onChange,
  label,
  errorMessage,
}: Props) => {
  const { t } = useResource();
  const [showBulkEdit, setShowBulkEdit] = useState(false);

  // Derive UI state from DTO value (single source of truth)
  const state = useMemo(
    () =>
      value && value.length > 0 ? dtoToState(value) : createEmptyHoursState(),
    [value],
  );

  // Transform UI state to DTO and notify parent
  const handleStateChange = (newState: HoursState) => {
    const dto = stateToDto(newState);
    onChange(dto);
  };

  const handleDayChange = (weekday: WeekDay, newDayHours: DayHours) => {
    const newState = {
      ...state,
      [weekday]: newDayHours,
    };
    handleStateChange(newState);
  };

  const handleBulkApply = (
    selectedDays: Array<WeekDay>,
    config: BulkHoursConfig,
  ) => {
    const newState = { ...state };
    for (const day of selectedDays) {
      newState[day] = {
        weekday: day,
        ranges: config.ranges,
        isOpenAllDay: config.isOpenAllDay,
        isClosedAllDay: config.isClosedAllDay,
      };
    }
    handleStateChange(newState);
  };

  return (
    <Box>
      {label && (
        <Typography variant="h6" sx={{ mb: 2 }}>
          {label}
        </Typography>
      )}

      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
        {t(RESOURCES.CurrentHours)}
      </Typography>
      <OpeningHoursSummary state={state} onDayChange={handleDayChange} />

      {showBulkEdit && (
        <BulkEditPanel
          onApply={handleBulkApply}
          onCancel={() => setShowBulkEdit(false)}
        />
      )}

      {!showBulkEdit && (
        <Button
          variant="outlined"
          onClick={() => setShowBulkEdit(!showBulkEdit)}
          sx={{ mb: 2 }}
        >
          {showBulkEdit ? t(RESOURCES.ExitBulkEdit) : t(RESOURCES.BulkEdit)}
        </Button>
      )}
      {errorMessage && (
        <StyledValidationText>{errorMessage}</StyledValidationText>
      )}
    </Box>
  );
};
